/**
 * 环境变量验证工具
 * 确保生产环境必须配置正确的域名和地址
 */
export class EnvValidator {
    /**
     * 验证环境变量
     * @param environment 环境类型
     * @param isProduction 是否为生产环境
     */
    static validate(_environment = 'development', isProduction = false) {
        const errors = [];
        const warnings = [];
        this.rules.forEach(rule => {
            const value = process.env[rule.key];
            // 检查是否必需
            if (rule.required && (!value || value.trim() === '')) {
                if (isProduction) {
                    errors.push(`🚨 ${rule.key} 是必需的，但未配置`);
                }
                else {
                    warnings.push(`⚠️ ${rule.key} 未配置，建议设置环境变量`);
                }
                return;
            }
            // 如果有值，进行验证
            if (value && rule.validator) {
                if (!rule.validator(value)) {
                    const errorMsg = rule.errorMessage || `${rule.key} 验证失败`;
                    if (isProduction) {
                        errors.push(`🚨 ${errorMsg}`);
                    }
                    else {
                        warnings.push(`⚠️ ${errorMsg}`);
                    }
                }
            }
        });
        // 输出警告（开发环境）
        if (!isProduction && warnings.length > 0) {
            console.warn('环境变量配置警告：');
            warnings.forEach(warning => console.warn(warning));
        }
        // 抛出错误（生产环境）
        if (isProduction && errors.length > 0) {
            console.error('生产环境配置错误：');
            errors.forEach(error => console.error(error));
            throw new Error(`生产环境配置验证失败：\n${errors.join('\n')}`);
        }
        // 成功验证
        if (isProduction) {
            console.log('✅ 生产环境配置验证通过');
        }
    }
    /**
     * 获取环境变量或抛出错误
     * @param key 环境变量键
     * @param fallback 默认值（仅开发环境）
     */
    static getRequiredEnv(key, fallback) {
        const value = process.env[key];
        const isProduction = process.env.NODE_ENV === 'production';
        if (!value || value.trim() === '') {
            if (isProduction) {
                throw new Error(`🚨 生产环境必须配置 ${key} 环境变量`);
            }
            else {
                if (fallback) {
                    console.warn(`⚠️ ${key} 未配置，使用默认值: ${fallback}`);
                    return fallback;
                }
                else {
                    throw new Error(`🚨 ${key} 环境变量未配置`);
                }
            }
        }
        return value;
    }
    /**
     * 验证 URL 是否有效且不是 localhost
     * @param url URL 字符串
     * @param allowLocalhost 是否允许 localhost（默认 false）
     */
    static validateUrl(url, allowLocalhost = false) {
        try {
            const urlObj = new URL(url);
            if (!allowLocalhost) {
                const isLocalhost = urlObj.hostname === 'localhost' ||
                    urlObj.hostname === '127.0.0.1' ||
                    urlObj.hostname.startsWith('192.168.') ||
                    urlObj.hostname.startsWith('10.') ||
                    urlObj.hostname.startsWith('172.');
                if (isLocalhost && process.env.NODE_ENV === 'production') {
                    return false;
                }
            }
            return true;
        }
        catch {
            return false;
        }
    }
    /**
     * 获取所有配置的环境变量摘要
     */
    static getConfigSummary() {
        const summary = {};
        this.rules.forEach(rule => {
            const value = process.env[rule.key];
            if (value) {
                // 隐藏敏感信息
                if (rule.key.includes('SECRET') || rule.key.includes('PASSWORD') || rule.key.includes('KEY')) {
                    summary[rule.key] = '***';
                }
                else {
                    summary[rule.key] = value;
                }
            }
        });
        return summary;
    }
}
EnvValidator.rules = [
    // 前端环境变量验证
    {
        key: 'REACT_APP_API_BASE_URL',
        required: true,
        validator: (value) => !value.includes('localhost'),
        errorMessage: '生产环境不能使用 localhost，请配置有效域名'
    },
    {
        key: 'REACT_APP_MSLOGIN_URL',
        required: true,
        validator: (value) => !value.includes('localhost'),
        errorMessage: '生产环境不能使用 localhost，请配置有效域名'
    },
    // 后端环境变量验证
    {
        key: 'CORS_ORIGIN',
        required: true,
        validator: (value) => value.includes('ihoms.cn'),
        errorMessage: '生产环境 CORS_ORIGIN 必须包含 ihoms.cn 域名'
    },
    {
        key: 'MSLOGIN_API_URL',
        required: true,
        validator: (value) => !value.includes('localhost'),
        errorMessage: '生产环境不能使用 localhost，请配置有效域名'
    },
    {
        key: 'REDIS_HOST',
        required: true,
        validator: (value) => !value.includes('localhost') && !value.includes('127.0.0.1'),
        errorMessage: '生产环境不能使用 localhost 或 127.0.0.1，请配置 Redis 服务器地址'
    },
    {
        key: 'HOSTNAME',
        required: true,
        validator: (value) => value.length > 0,
        errorMessage: '生产环境必须配置 HOSTNAME'
    }
];
// 自动验证（如果环境变量启用）
if (process.env.ENABLE_ENV_VALIDATION === 'true') {
    const isProduction = process.env.NODE_ENV === 'production';
    try {
        EnvValidator.validate(process.env.NODE_ENV, isProduction);
    }
    catch (error) {
        console.error('环境变量验证失败:', error);
        if (isProduction) {
            process.exit(1); // 生产环境验证失败则退出
        }
    }
}
